/**
 * Direct vote service for instant vote submission.
 * Calls the /api/vote endpoint immediately without queuing.
 */

import { getUserId } from './user';

const API_BASE = import.meta.env.VITE_SYNC_API_URL ||
  import.meta.env.VITE_PHOTO_API_URL ||
  'https://where-in-the-world-photos.armincerf.workers.dev';

interface VoteResult {
  ok: boolean;
  changed: boolean;
  countryCode?: string;
  rating?: number;
  error?: string;
}

/**
 * Submit a vote immediately to the server.
 * This bypasses the sync queue and writes directly to the database,
 * triggering a real-time broadcast to all leaderboard clients.
 */
export async function submitVote(countryCode: string, rating: number): Promise<VoteResult> {
  const userId = getUserId();

  try {
    const response = await fetch(`${API_BASE}/api/vote`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        userId,
        countryCode,
        rating,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        ok: false,
        changed: false,
        error: errorData.error || `HTTP ${response.status}`,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Vote submission failed:', error);
    return {
      ok: false,
      changed: false,
      error: error instanceof Error ? error.message : 'Network error',
    };
  }
}
